import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Popover } from '../ui';
import { chosenList, chosenNamesTip } from '../../utils/branchCompare';

/** With more branches than this the picker gets a search box. */
export const CHOSEN_SEARCH_MIN = 8;

const Caret = () => (
    <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">
        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
    </svg>
);

/**
 * "Compare with: Minot, Rugby" — the branches the "Chosen branches" basis
 * compares the selection with (docs/decisions/branch-compare.md, "Chosen
 * branches"). A popover (ui/Popover: focus moves in, Escape and an outside
 * click close it) with a checkbox per branch in page order; the selection's
 * own branches are disabled ("in your selection"). A search box filters the
 * list once there are more than CHOSEN_SEARCH_MIN branches. `chosen` and
 * `onChange` use names in page order; `open` / `onOpenChange` let the empty
 * prompt below open it.
 */
export default function ChosenPicker({
    allNames, selectedNames, chosen, onChange, open, onOpenChange,
}) {
    const [query, setQuery] = useState('');
    const wrapRef = useRef(null);
    useEffect(() => {
        if (!open) setQuery('');
    }, [open]);

    const self = new Set(selectedNames);
    const picked = new Set(chosen);
    const q = query.trim().toLowerCase();
    const shown = q ? allNames.filter((nm) => nm.toLowerCase().includes(q)) : allNames;
    const toggle = (name) => {
        const next = new Set(picked);
        if (next.has(name)) next.delete(name); else next.add(name);
        onChange(allNames.filter((nm) => next.has(nm)));
    };
    // Enter toggles a focused checkbox, as Space does (QA chosen #4).
    const onBoxKey = (name) => (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        toggle(name);
    };
    const done = () => {
        onOpenChange(false);
        wrapRef.current?.querySelector('button[aria-expanded]')?.focus();
    };

    return (
        <span className="br2-chs-w" ref={wrapRef}>
            <Popover
                triggerLabel="Compare with"
                title="Compare with"
                width={280}
                open={open}
                onOpenChange={onOpenChange}
                triggerClassName={`v2-btn br2-chs-btn${chosen.length ? ' is-set' : ''}`}
                triggerContent={(
                    <>
                        <span className="br2-chs-bl">Compare with:</span>
                        {' '}
                        <span className="br2-chs-bv" title={chosenNamesTip(chosen) || undefined}>{chosen.length ? chosenList(chosen) : 'pick branches'}</span>
                        <Caret />
                    </>
                )}
            >
                <div className="br2-chs-head">
                    <h3 className="v2-pop-title">Compare with</h3>
                    <span className="br2-chs-n" aria-live="polite">{chosen.length} picked</span>
                </div>
                {allNames.length > CHOSEN_SEARCH_MIN && (
                    <label className="v2-search br2-chs-find">
                        <span className="v2-sr">Find a branch</span>
                        <input
                            type="search"
                            placeholder="Find a branch"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            autoComplete="off"
                        />
                    </label>
                )}
                <div className="br2-chs-list" role="group" aria-label="Branches to compare with">
                    {shown.map((name) => {
                        const mine = self.has(name);
                        return (
                            <label key={name} className={mine ? 'is-self' : undefined}>
                                <input
                                    type="checkbox"
                                    checked={!mine && picked.has(name)}
                                    disabled={mine}
                                    onChange={() => toggle(name)}
                                    onKeyDown={onBoxKey(name)}
                                />
                                <span className="br2-chs-nm">{name}</span>
                                {mine && <span className="br2-chs-self">in your selection</span>}
                            </label>
                        );
                    })}
                    {!shown.length && <p className="br2-chs-none">No branch matches &quot;{query.trim()}&quot;.</p>}
                </div>
                <div className="br2-chs-foot">
                    <button type="button" className="v2-clear" onClick={() => onChange([])} disabled={!chosen.length}>Clear</button>
                    <button type="button" className="v2-btn v2-btn--primary" onClick={done}>Done</button>
                </div>
            </Popover>
        </span>
    );
}

ChosenPicker.propTypes = {
    allNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    chosen: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    onOpenChange: PropTypes.func.isRequired,
};
